<template>
    <div class="animated fadeIn content-management-page">
        <div v-if="loading" class="loading-container">
            <atom-spinner
                :animation-duration="1000"
                :size="60"
                color="#ff1d5e"
            />
        </div>

        <div v-else>
            <component-header
                title="Blogs"
                @search-query="(val) => (searchFilter = val)"
                :isLoading="loading"
                @reload-data="getBlogsList"
                :hasSearch="true"
                :hasReload="true"
                :hasCreate="true"
                :hasback="true"
                @back-press="backBtn"
                createButtonText="Create Blogs"
                @create-press="
                    $router.push({ name: 'content-management-create' })
                "
            >
                <template #heading-before-search>
                    <b-form-select
                        v-model="statusFilter"
                        :options="statusOptions"
                        class="content-filter-select"
                    />
                </template>
            </component-header>

            <b-row class="stats-row mt-3">
                <b-col sm="6" lg="3" class="mb-3 stats-col">
                    <b-card class="card-wrap stat-card">
                        <div class="stat-content">
                            <div class="stat-icon total-posts">
                                <i class="fas fa-file-alt"></i>
                            </div>
                            <div class="stat-info">
                                <div class="stat-label">Total Posts</div>
                                <div class="stat-value">{{ totalPosts }}</div>
                            </div>
                        </div>
                    </b-card>
                </b-col>
                <b-col sm="6" lg="3" class="mb-3 stats-col">
                    <b-card class="card-wrap stat-card">
                        <div class="stat-content">
                            <div class="stat-icon published">
                                <i class="fas fa-eye"></i>
                            </div>
                            <div class="stat-info">
                                <div class="stat-label">Published</div>
                                <div class="stat-value">
                                    {{ publishedCount }}
                                </div>
                            </div>
                        </div>
                    </b-card>
                </b-col>
                <b-col sm="6" lg="3" class="mb-3 stats-col">
                    <b-card class="card-wrap stat-card">
                        <div class="stat-content">
                            <div class="stat-icon drafts">
                                <i class="fas fa-paperclip"></i>
                            </div>
                            <div class="stat-info">
                                <div class="stat-label">Drafts</div>
                                <div class="stat-value">{{ draftsCount }}</div>
                            </div>
                        </div>
                    </b-card>
                </b-col>
                <b-col sm="6" lg="3" class="mb-3 stats-col">
                    <b-card class="card-wrap stat-card">
                        <div class="stat-content">
                            <div class="stat-icon views">
                                <i class="fas fa-eye"></i>
                            </div>
                            <div class="stat-info">
                                <div class="stat-label">Total Views</div>
                                <div class="stat-value">{{ totalViews }}</div>
                            </div>
                        </div>
                    </b-card>
                </b-col>
            </b-row>

            <div v-if="paginatedBlogs.length > 0">
                <b-card
                    v-for="blog in paginatedBlogs"
                    :key="blog._id"
                    class="card-wrap blog-card mb-3"
                >
                    <b-row class="align-items-center">
                        <b-col md="2" class="mb-3 mb-md-0">
                            <div class="blog-thumbnail">
                                <img
                                    :src="getBlogThumbnail(blog)"
                                    :alt="blog.title"
                                    class="thumbnail-image"
                                />
                            </div>
                        </b-col>

                        <b-col md="7">
                            <div class="blog-content">
                                <div class="blog-header">
                                    <h5 class="blog-title">
                                        {{ blog.title || "-" }}
                                    </h5>
                                    <b-badge
                                        :variant="getStatusVariant(blog.status)"
                                        class="status-badge"
                                    >
                                        {{ getStatusText(blog.status) }}
                                    </b-badge>
                                </div>
                                <p class="blog-description">
                                    {{ getBlogDescription(blog) }}
                                </p>
                                <div
                                    class="blog-tags"
                                    v-if="
                                        blog?.meta?.extraDetails?.tags &&
                                        blog.meta.extraDetails.tags.length
                                    "
                                >
                                    <b-badge
                                        v-for="tag in blog.meta.extraDetails
                                            .tags"
                                        :key="tag"
                                        variant="secondary"
                                        class="tag-badge mr-2 mb-2"
                                    >
                                        {{ tag }}
                                    </b-badge>
                                </div>

                                <div class="blog-meta">
                                    <span class="meta-item">
                                        <i class="fas fa-user mr-1"></i>
                                        {{ getBlogAuthor(blog) }}
                                    </span>
                                    <span class="meta-item">
                                        <i class="fas fa-calendar mr-1"></i>
                                        {{ formatDateShort(blog.createdAt) }}
                                    </span>
                                    <span class="meta-item">
                                        <i class="fas fa-eye mr-1"></i>
                                        {{ getBlogViews(blog) }} views
                                    </span>
                                </div>
                            </div>
                        </b-col>

                        <b-col md="3" class="text-md-right">
                            <div class="blog-actions">
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    class="action-btn mb-2"
                                    @click="viewBlog(blog)"
                                >
                                    <i class="fas fa-eye mr-1"></i>View
                                </b-button>
                                <b-button
                                    variant="outline-secondary"
                                    size="sm"
                                    class="action-btn mb-2"
                                    @click="editBlog(blog)"
                                >
                                    <i class="fas fa-pencil-alt mr-1"></i>Edit
                                </b-button>
                                <b-button
                                    variant="outline-danger"
                                    size="sm"
                                    class="action-btn mb-2"
                                    @click="deleteBlogItem(blog)"
                                >
                                    <i class="fas fa-trash-alt mr-1"></i>Delete
                                </b-button>
                            </div>
                        </b-col>
                    </b-row>
                </b-card>
            </div>

            <b-card v-else class="card-wrap text-center py-5" border-variant="info">
                <i class="fas fa-file-alt fa-3x text-muted mb-3"></i>
                <p class="text-muted">No blog posts found</p>
            </b-card>

            <div class="pagination-wrapper" v-if="filterCount > perPage">
                <b-pagination
                    v-model="currentPage"
                    :per-page="perPage"
                    :total-rows="filterCount"
                    aria-controls="blogs"
                    prev-text="Prev"
                    next-text="Next"
                    hide-goto-end-buttons
                    class="custom-pagination"
                ></b-pagination>
            </div>
        </div>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";
import { mapGetters } from "vuex";
import { AtomSpinner } from "epic-spinners";
import moment from "moment";

export default {
    name: "ContentManagement",
    components: {
        ComponentHeader,
        AtomSpinner,
    },
    data() {
        return {
            loading: false,
            currentPage: 1,
            searchFilter: "",
            statusFilter: "all",
            perPage: 10,
            statusOptions: [
                { value: "all", text: "All Status" },
                { value: "2", text: "Published" },
                { value: "0", text: "Draft" },
            ],
        };
    },
    computed: {
        ...mapGetters({
            BlogList: "blog/GetBlogList",
        }),

        blogs() {
            const list = Array.isArray(this.BlogList)
                ? this.BlogList
                : [this.BlogList];

            return list.filter(Boolean).map((blog) => ({
                ...blog,
                author: blog?.meta?.extraDetails?.author || blog?.author || "-",
            }));
        },

        totalPosts() {
            return this.blogs.length;
        },

        publishedCount() {
            return this.blogs.filter((blog) => blog.status === 2).length;
        },

        draftsCount() {
            return this.blogs.filter((blog) => blog.status !== 2).length;
        },

        totalViews() {
            return this.blogs
                .reduce((sum, blog) => {
                    const views = blog?.stat?.requests || 0;
                    return sum + views;
                }, 0)
                .toLocaleString();
        },

        filteredBlogs() {
            let filtered = this.blogs;

            if (this.statusFilter !== "all") {
                filtered = filtered.filter(
                    (blog) => String(blog.status) === this.statusFilter,
                );
            }

            if (this.searchFilter) {
                const term = this.searchFilter.toLowerCase();
                filtered = filtered.filter(
                    (blog) =>
                        (blog.title &&
                            blog.title.toLowerCase().includes(term)) ||
                        (blog?.meta?.extraDetails?.author &&
                            blog.meta.extraDetails.author
                                .toLowerCase()
                                .includes(term)) ||
                        (blog?.meta?.extraDetails?.excerpt &&
                            blog.meta.extraDetails.excerpt
                                .toLowerCase()
                                .includes(term)),
                );
            }

            return filtered;
        },

        paginatedBlogs() {
            const start = (this.currentPage - 1) * this.perPage;
            return this.filteredBlogs.slice(start, start + this.perPage);
        },

        filterCount() {
            return this.filteredBlogs.length;
        },
    },

    methods: {
        formatDate(date) {
            if (!date) return "-";
            return moment(date).format("DD MMM YYYY, HH:mm");
        },
        formatDateShort(date) {
            if (!date) return "-";
            return moment(date).format("YYYY-MM-DD");
        },
        getBlogThumbnail(blog) {
            if (
                blog.media &&
                Array.isArray(blog.media) &&
                blog.media.length > 0
            ) {
                const imageMedia = blog.media.find(
                    (m) => m.type === "IMAGE" && m.value,
                );
                if (imageMedia) return imageMedia.value;
            }
            return "/img/placeholder.png";
        },
        getBlogDescription(blog) {
            if (blog?.meta?.extraDetails?.excerpt) {
                return blog.meta.extraDetails.excerpt;
            }
            if (blog.content) {
                const text = blog.content.replace(/<[^>]*>/g, "");
                return text.length > 150
                    ? text.substring(0, 150) + "..."
                    : text;
            }
            return "No description available";
        },
        getBlogAuthor(blog) {
            return (
                blog?.meta?.extraDetails?.author ||
                blog?.user?.name ||
                blog?.author ||
                "-"
            );
        },
        getBlogViews(blog) {
            return blog?.stat?.requests || 0;
        },
        getStatusText(status) {
            const statusMap = {
                0: "Draft",
                1: "Draft",
                2: "Published",
            };
            return statusMap[status] || "Unknown";
        },
        getStatusVariant(status) {
            const variantMap = {
                1: "warning",
                // 1: "secondary",
                2: "success",
            };
            return variantMap[status] || "secondary";
        },
        viewBlog(blog) {
            this.$router.push({
                name: "content-management-edit",
                params: { id: blog._id || blog.id },
            });
        },
        editBlog(data) {
            this.$router.push({
                name: "content-management-edit",
                params: { id: data._id || data.id },
            });
        },
        deleteBlogItem(data) {
            this.$swal({
                title: "Delete this Blog?",
                text: "Are you sure? You won't be able to revert this!",
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, Delete it!",
                reverseButtons: true,
            }).then((result) => {
                if (result.value) {
                    this.$store
                        .dispatch("blog/deleteBlog", data._id || data.id)
                        .then(() => {
                            this.$toasted.success("Blog Deleted Successfully!");
                            this.getBlogsList();
                        })
                        .catch(() => {
                            this.$toasted.error("Blog not deleted");
                        });
                }
            });
        },
        getBlogsList() {
            this.loading = true;
            this.$store.dispatch("blog/fetchBlogsList").finally(() => {
                this.loading = false;
            });
        },
        backBtn() {
            this.$router.push("/dashboard");
        },
    },

    mounted() {
        this.getBlogsList();
    },
};
</script>

<style scoped>
.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.card-wrap {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
}

.content-filter-select {
    min-width: 160px;
    height: 34px;
    font-size: 14px;
    border-radius: 8px;
    border: 1px solid var(--slate-200, #e2e8f0);
}

.stat-card {
    border: 1px solid var(--slate-200, #e2e8f0);
    background: #fff;
    height: 100%;
    width: 100%;
}

.stat-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-direction: row;
}

.stat-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
}

.stat-icon.total-posts {
    background-color: var(--primary, #3b82f6);
}

.stat-icon.published {
    background-color: var(--success, #10b981);
}

.stat-icon.drafts {
    background-color: var(--warning, #f59e0b);
}

.stat-icon.views {
    background-color: var(--primary, #3b82f6);
}

.stat-info {
    flex: 1;
    min-width: 0;
}

.stat-label {
    font-size: 0.875rem;
    color: var(--slate-600, #475569);
    margin-bottom: 0.25rem;
}

.stat-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--slate-900, #0f172a);
}

.blog-card {
    border: 1px solid var(--slate-200, #e2e8f0);
    transition: box-shadow 0.2s;
}

.blog-card:hover {
    box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.08),
        0 2px 4px -1px rgba(0, 0, 0, 0.05);
}

.blog-thumbnail {
    width: 100%;
    height: 150px;
    border-radius: 8px;
    overflow: hidden;
    background-color: var(--slate-100, #f1f5f9);
}

.thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.blog-content {
    padding: 0.5rem 0;
}

.blog-header {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    flex-wrap: nowrap;
}

.blog-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--slate-900, #0f172a);
    margin: 0;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.status-badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    flex-shrink: 0;
}

.blog-description {
    color: var(--slate-600, #475569);
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
    line-height: 1.5;
}

.blog-tags {
    margin-bottom: 0.75rem;
}

.tag-badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
}

.blog-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    font-size: 0.875rem;
    color: var(--slate-600, #475569);
}

.meta-item {
    display: flex;
    align-items: center;
}

.blog-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.action-btn {
    width: 100%;
    border-radius: 10px;
    font-size: 0.875rem;
}

.pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
}

::v-deep .custom-pagination .page-item.active .page-link {
    background: var(--primary, #2563eb);
    border-color: var(--primary, #2563eb);
    color: #fff;
}

::v-deep .custom-pagination .page-link {
    color: var(--slate-700, #334155);
}

@media (max-width: 767.98px) {
    .stat-content {
        flex-direction: column;
        text-align: center;
    }

    .blog-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .blog-actions {
        flex-direction: row;
        justify-content: flex-start;
    }

    .action-btn {
        width: auto;
        flex: 1;
    }

    .pagination-wrapper {
        justify-content: center;
    }

    .blog-meta {
        flex-direction: column;
        gap: 0.5rem;
    }
}

@media (max-width: 575.98px) {
    .stat-value {
        font-size: 1.25rem;
    }

    .blog-title {
        font-size: 1rem;
    }

    .action-btn {
        font-size: 0.75rem;
        padding: 0.375rem 0.5rem;
    }
}
</style>
